import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import './Legal.css';

const TermsOfService = () => {
  return (
    <>
      <SEO
        title="Terms of Service"
        description="Terms of Service for SilverFox Media. Read our terms governing the use of our website templates, development services, and hosting plans."
        url="/terms-of-service"
      />

      {/* Hero Section */}
      <section className="legal-hero">
        <div className="legal-hero__bg">
          <div className="legal-hero__gradient-orb legal-hero__gradient-orb--1" />
          <div className="legal-hero__gradient-orb legal-hero__gradient-orb--2" />
        </div>
        <div className="container">
          <motion.div
            className="legal-hero__content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1>Terms of Service</h1>
            <p>Last updated: January 15, 2026</p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="legal-content section">
        <div className="container">
          <motion.div
            className="legal-content__wrapper"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="legal-section">
              <h2>1. Agreement to Terms</h2>
              <p>
                By accessing or using SilverFox Media's website, purchasing our templates, or engaging our services, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.
              </p>
              <p>
                These terms apply to all visitors, users, and customers of SilverFox Media, including those who purchase website templates, custom development services, or hosting plans.
              </p>
            </div>

            <div className="legal-section">
              <h2>2. Services Provided</h2>
              <p>SilverFox Media provides the following services:</p>
              <ul>
                <li><strong>Website Templates:</strong> Pre-designed HTML/CSS website templates available for purchase and download.</li>
                <li><strong>Full Setup Services:</strong> Customization and deployment of templates including branding, content, and domain setup.</li>
                <li><strong>Custom Development:</strong> Bespoke web application development, redesigns, and integrations.</li>
                <li><strong>Hosting & Maintenance:</strong> Ongoing website hosting, security updates, backups, and support.</li>
                <li><strong>Analytics Setup:</strong> Google Analytics 4 and Search Console configuration with custom dashboards.</li>
              </ul>
            </div>

            <div className="legal-section">
              <h2>3. Template License Terms</h2>
              <p>When you purchase a template from SilverFox Media, you are granted a limited, non-exclusive license subject to the following terms:</p>
              <ul>
                <li><strong>Single Use:</strong> Each template license is valid for ONE website or project only.</li>
                <li><strong>No Resale:</strong> You may not resell, redistribute, or share the template files with third parties.</li>
                <li><strong>No Sublicensing:</strong> You may not sublicense the template to others.</li>
                <li><strong>Modification Allowed:</strong> You may modify the template for your own use on the licensed project.</li>
                <li><strong>Attribution:</strong> The "Designed & Built by SilverFox Media" footer credit must remain intact unless removed as part of a paid Full Setup service.</li>
              </ul>
              <p>
                Violation of these license terms will result in immediate termination of your license and may result in legal action.
              </p>
            </div>

            <div className="legal-section">
              <h2>4. Payment Terms</h2>
              <h3>Template Purchases</h3>
              <p>
                All template purchases are processed through Stripe. Payment is required in full before template files are delivered. Prices are listed in USD and are subject to change without notice.
              </p>
              <h3>Full Setup & Custom Services</h3>
              <p>
                For Full Setup services, payment is required upfront before work begins. For custom development projects exceeding $2,500, we may offer a 50% deposit with the balance due upon completion.
              </p>
              <h3>Hosting & Maintenance</h3>
              <p>
                Hosting fees are billed monthly in advance. Failure to pay hosting fees may result in suspension or termination of hosting services after a 7-day grace period.
              </p>
            </div>

            <div className="legal-section">
              <h2>5. Refund Policy</h2>
              <p>
                Please review our <Link to="/refund-policy">Refund Policy</Link> for complete details. In summary:
              </p>
              <ul>
                <li><strong>Templates:</strong> No refunds once template files have been downloaded.</li>
                <li><strong>Services:</strong> Refunds for services are handled on a case-by-case basis.</li>
                <li><strong>Hosting:</strong> No refunds for partial months of hosting service.</li>
              </ul>
            </div>

            <div className="legal-section">
              <h2>6. Intellectual Property</h2>
              <h3>Our Property</h3>
              <p>
                All templates, code, designs, logos, and content created by SilverFox Media remain the intellectual property of SilverFox Media until properly licensed or transferred. The SilverFox Media name, logo, and branding are trademarks of SilverFox Media.
              </p>
              <h3>Your Property</h3>
              <p>
                Content you provide (text, images, logos) remains your property. By providing content for your project, you grant us permission to use it solely for completing your project.
              </p>
              <h3>Custom Development</h3>
              <p>
                For custom development projects, ownership of the final deliverables transfers to you upon full payment, unless otherwise specified in a separate agreement.
              </p>
            </div>

            <div className="legal-section">
              <h2>7. Client Responsibilities</h2>
              <p>When engaging our services, you agree to:</p>
              <ul>
                <li>Provide accurate and complete information as requested.</li>
                <li>Provide necessary content, images, and materials in a timely manner.</li>
                <li>Review and provide feedback on deliverables within reasonable timeframes.</li>
                <li>Ensure you have the right to use any content you provide.</li>
                <li>Maintain the security of any login credentials we provide.</li>
              </ul>
            </div>

            <div className="legal-section">
              <h2>8. Limitation of Liability</h2>
              <p>
                To the maximum extent permitted by law, SilverFox Media shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or business opportunities.
              </p>
              <p>
                Our total liability for any claim arising from our services shall not exceed the amount you paid for the specific service giving rise to the claim.
              </p>
              <p>
                We do not guarantee that our templates or services will be error-free, uninterrupted, or meet your specific requirements.
              </p>
            </div>

            <div className="legal-section">
              <h2>9. Indemnification</h2>
              <p>
                You agree to indemnify, defend, and hold harmless SilverFox Media and its owner, employees, and agents from any claims, damages, losses, or expenses arising from:
              </p>
              <ul>
                <li>Your use of our templates or services.</li>
                <li>Your violation of these Terms of Service.</li>
                <li>Your violation of any third-party rights.</li>
                <li>Content you provide that infringes on intellectual property rights.</li>
              </ul>
            </div>

            <div className="legal-section">
              <h2>10. Termination</h2>
              <p>
                We reserve the right to terminate or suspend your access to our services at our sole discretion, without notice, for conduct that we believe violates these Terms of Service or is harmful to other users, us, or third parties.
              </p>
              <p>
                For hosting clients, we will provide 30 days notice before terminating service, except in cases of non-payment or Terms of Service violations.
              </p>
            </div>

            <div className="legal-section">
              <h2>11. Governing Law</h2>
              <p>
                These Terms of Service shall be governed by and construed in accordance with the laws of the State of Texas, without regard to its conflict of law provisions. Any disputes arising from these terms shall be resolved in the courts of Dallas County, Texas.
              </p>
            </div>

            <div className="legal-section">
              <h2>12. Changes to Terms</h2>
              <p>
                We reserve the right to modify these Terms of Service at any time. Changes will be effective immediately upon posting to our website. Your continued use of our services after changes are posted constitutes acceptance of the modified terms.
              </p>
            </div>

            <div className="legal-section">
              <h2>13. Contact Information</h2>
              <p>
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <ul className="legal-contact">
                <li><strong>Email:</strong> james@silverfoxmedia.co</li>
                <li><strong>Phone:</strong> (972) 800-8105</li>
                <li><strong>Website:</strong> silverfoxmedia.co</li>
              </ul>
            </div>

          </motion.div>
        </div>
      </section>
    </>
  );
};

export default TermsOfService;
